import { Button, useToast } from '@citizen-walk/shared-ui';
import type { IssueMcpTokenResult } from '@citizen-walk/shared-types';

/**
 * 発行直後の MCP トークンを **1回だけ** 表示するモーダル。
 * raw token は再表示不可のため、コピー導線と接続手順 (endpoint / 設定コマンド) を併せて提示する。
 */
export interface IssuedTokenModalProps {
  result: IssueMcpTokenResult;
  /** MCP サーバーの endpoint URL (Cloud Functions 直 URL)。 */
  endpointUrl: string;
  onClose: () => void;
}

export function IssuedTokenModal({ result, endpointUrl, onClose }: IssuedTokenModalProps) {
  const { toast } = useToast();

  const copy = (value: string, label: string) => {
    void navigator.clipboard
      .writeText(value)
      .then(() => toast('success', `${label}をコピーしました`))
      .catch(() => toast('error', 'コピーに失敗しました'));
  };

  const claudeCmd = `claude mcp add --transport http citizenwalk ${endpointUrl} --header "Authorization: Bearer ${result.token}"`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-lg max-h-[90vh] overflow-auto rounded-lg bg-white p-5 shadow-xl">
        <h3 className="text-base font-bold text-slate-900">アクセストークンを発行しました</h3>
        <p className="mt-1 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 leading-relaxed">
          ⚠️ このトークンは <strong>この画面でしか確認できません</strong>
          。閉じると二度と表示されません。今すぐコピーして、利用する AI
          チャットの設定に貼り付けてください。紛失した場合はこのトークンを失効して新しく発行してください。
        </p>

        <div className="mt-4 grid grid-cols-1 gap-1">
          <span className="text-xs font-semibold text-slate-700">
            トークン（ラベル: {result.label}）
          </span>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 break-all rounded border border-slate-300 bg-slate-50 p-2 text-[11px] text-slate-800">
              {result.token}
            </code>
            <Button variant="secondary" size="sm" onClick={() => copy(result.token, 'トークン')}>
              コピー
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-1">
          <span className="text-xs font-semibold text-slate-700">接続先（endpoint URL）</span>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 break-all rounded border border-slate-300 bg-slate-50 p-2 text-[11px] text-slate-800">
              {endpointUrl}
            </code>
            <Button variant="secondary" size="sm" onClick={() => copy(endpointUrl, '接続先')}>
              コピー
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-1">
          <span className="text-xs font-semibold text-slate-700">
            Claude Code に登録する場合のコマンド例
          </span>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 break-all rounded border border-slate-300 bg-slate-50 p-2 text-[11px] text-slate-800">
              {claudeCmd}
            </code>
            <Button variant="secondary" size="sm" onClick={() => copy(claudeCmd, 'コマンド')}>
              コピー
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Claude Desktop / ChatGPT / Gemini などからは、各アプリの「コネクタ追加 / MCP
            サーバー追加」で上記の接続先 URL とトークンを設定してください。
          </p>
        </div>

        <div className="mt-5 flex justify-end">
          <Button variant="primary" onClick={onClose}>
            コピーしました・閉じる
          </Button>
        </div>
      </div>
    </div>
  );
}
